import {
  inject,
  OnInit,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewInit,
  ViewContainerRef,
} from '@angular/core';

import { faUserPlus, faUserShield } from '@fortawesome/free-solid-svg-icons';

import { Store } from '@ngrx/store';

import { UtilsService } from 'src/app/core/services/utils.service';

import { ObjectPermissionsComponent } from '../../object-permissions/object-permissions.component';

import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';

import { DataObject } from 'src/app/core/models/core.models';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';
import { TabConfig, SearchableDialog } from 'src/app/shared/utils';

import { saveObjectPermissions } from 'src/app/core/actions/core.actions';

import { AppState } from 'src/app/reducers';

interface DialogData extends TabDialogDataBase {
  objects: DataObject[];
}

interface DialogResult {}

@Component({
  selector: 'app-object-permissions-dialog',
  templateUrl: './object-permissions-dialog.component.html',
  styleUrl: './object-permissions-dialog.component.scss',
  standalone: false,
})
export class ObjectPermissionsDialogComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit, AfterViewInit
{
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);
  private utilsService = inject(UtilsService);

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  actionIcon = faUserShield;
  objectIcon = faUserPlus;
  actionTitle = this.translateService.instant(
    'object-permission-popup.title.permission-editor',
  );
  objectTitle = '';

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  get objects(): DataObject[] {
    return this.dialogData.objects;
  }

  ngOnInit(): void {
    this.setupUIState();
    this.setupTabs();
  }

  ngAfterViewInit(): void {
    this.initializeBase();
    this.tabSearch.setupAutoSwitch(this.tabs);
  }

  setupUIState(): void {
    this.objectTitle = this.objects.map((object) => object.title).join(', ');
  }

  setupTabs(): void {
    this.tabs.push({
      id: 'permissions',
      label: this.translateService.instant(
        'object-permissions-popup.tab.permissions',
      ),
      component: ObjectPermissionsComponent,
      inputs: {
        searchString$: this.searchString$,
        objects: this.objects,
      },
    });
  }

  protected override performSave(
    formData: any,
  ): DialogCloseOptions<DialogResult> {
    let permissions = this.utilsService.convertArrayKeysToNested(formData);

    this.store.dispatch(
      saveObjectPermissions({
        permissions: permissions['permission'],
        objects: this.objects,
      }),
    );

    return {
      reason: DialogCloseReason.Save,
      data: {},
    };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);
    this.dirtyHandler.closeImmediately(closeOptions);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
